import { create } from "zustand";

export type GraphicSide = "outside" | "inside";

export type DraftGraphicCommand = {
	id: string;
	target: "item" | "sku";
	collectionId: string;
	itemId: string;
	sku?: string;
	side: GraphicSide;
	file: File;
	previewUrl: string;
};

export type CollectionPatchPayload = {
	client?: string;
	name?: string;
	fulfillAt?: string | null;
	fulfillBy?: string | null;
};

export type ItemPatchPayload = {
	number?: string;
	recipient?: Record<string, unknown>;
	message?: string | null;
	addons?: string[];
	product?: Record<string, unknown>;
	graphics?: {
		outside: string | null;
		inside: string | null;
	};
};

export type QueuedCollectionMutation =
	| {
			id: string;
			type: "create-collection";
			payload: Record<string, unknown>;
	  }
	| {
			id: string;
			type: "patch-collection";
			collectionId: string;
			payload: CollectionPatchPayload;
	  }
	| {
			id: string;
			type: "delete-collection";
			collectionId: string;
	  }
	| {
			id: string;
			type: "toggle-draft";
			collectionId: string;
	  }
	| {
			id: string;
			type: "patch-item";
			collectionId: string;
			itemId: string;
			payload: ItemPatchPayload;
	  }
	| {
			id: string;
			type: "patch-item-graphics";
			command: DraftGraphicCommand;
	  };

type QueueStore = {
	queuedMutations: QueuedCollectionMutation[];
	enqueueCreateCollection: (payload: Record<string, unknown>) => void;
	enqueuePatchCollection: (collectionId: string, payload: CollectionPatchPayload) => void;
	enqueueDeleteCollection: (collectionId: string) => void;
	enqueueToggleDraft: (collectionId: string) => void;
	enqueuePatchItem: (collectionId: string, itemId: string, payload: ItemPatchPayload) => void;
	upsertItemGraphic: (input: {
		collectionId: string;
		itemId: string;
		side: GraphicSide;
		file: File;
		previewUrl: string;
	}) => void;
	upsertSkuGraphic: (input: {
		collectionId: string;
		itemId: string;
		sku: string;
		side: GraphicSide;
		file: File;
		previewUrl: string;
	}) => void;
	findPreviewUrl: (
		collectionId: string,
		itemId: string,
		sku: string | undefined,
		side: GraphicSide,
	) => string | null;
	clearQueue: () => void;
	clearCollectionQueue: (collectionId: string) => void;
};

const revokePreview = (mutation: QueuedCollectionMutation) => {
	if (mutation.type !== "patch-item-graphics") return;
	URL.revokeObjectURL(mutation.command.previewUrl);
};

export const useCollectionMutationQueueStore = create<QueueStore>((set, get) => ({
	queuedMutations: [],
	enqueueCreateCollection: (payload) => {
		set((state) => ({
			queuedMutations: [
				...state.queuedMutations.filter((mutation) => mutation.type !== "create-collection"),
				{ id: crypto.randomUUID(), type: "create-collection", payload },
			],
		}));
	},
	enqueuePatchCollection: (collectionId, payload) => {
		set((state) => {
			const existingIndex = state.queuedMutations.findIndex(
				(mutation) =>
					mutation.type === "patch-collection" && mutation.collectionId === collectionId,
			);
			if (existingIndex === -1) {
				return {
					queuedMutations: [
						...state.queuedMutations,
						{ id: crypto.randomUUID(), type: "patch-collection", collectionId, payload },
					],
				};
			}
			return {
				queuedMutations: state.queuedMutations.map((mutation, index) => {
					if (index !== existingIndex || mutation.type !== "patch-collection") return mutation;
					return { ...mutation, payload: { ...mutation.payload, ...payload } };
				}),
			};
		});
	},
	enqueueDeleteCollection: (collectionId) => {
		set((state) => {
			const next = state.queuedMutations.filter((mutation) => {
				if (
					mutation.type === "patch-item-graphics" &&
					mutation.command.collectionId === collectionId
				) {
					revokePreview(mutation);
					return false;
				}
				if ("collectionId" in mutation) {
					return mutation.collectionId !== collectionId;
				}
				return mutation.type !== "create-collection";
			});
			next.push({ id: crypto.randomUUID(), type: "delete-collection", collectionId });
			return { queuedMutations: next };
		});
	},
	enqueueToggleDraft: (collectionId) => {
		set((state) => ({
			queuedMutations: [
				...state.queuedMutations,
				{ id: crypto.randomUUID(), type: "toggle-draft", collectionId },
			],
		}));
	},
	enqueuePatchItem: (collectionId, itemId, payload) => {
		set((state) => {
			const existingIndex = state.queuedMutations.findIndex(
				(mutation) =>
					mutation.type === "patch-item" &&
					mutation.collectionId === collectionId &&
					mutation.itemId === itemId,
			);
			if (existingIndex === -1) {
				return {
					queuedMutations: [
						...state.queuedMutations,
						{ id: crypto.randomUUID(), type: "patch-item", collectionId, itemId, payload },
					],
				};
			}
			return {
				queuedMutations: state.queuedMutations.map((mutation, index) => {
					if (index !== existingIndex || mutation.type !== "patch-item") return mutation;
					return { ...mutation, payload: { ...mutation.payload, ...payload } };
				}),
			};
		});
	},
	upsertItemGraphic: (input) => {
		set((state) => {
			let replacedPreview: string | null = null;
			const next = state.queuedMutations.filter((mutation) => {
				if (mutation.type !== "patch-item-graphics") return true;
				const match =
					mutation.command.target === "item" &&
					mutation.command.collectionId === input.collectionId &&
					mutation.command.itemId === input.itemId &&
					mutation.command.side === input.side;
				if (match) {
					replacedPreview = mutation.command.previewUrl;
				}
				return !match;
			});
			if (replacedPreview) URL.revokeObjectURL(replacedPreview);
			next.push({
				id: crypto.randomUUID(),
				type: "patch-item-graphics",
				command: {
					id: crypto.randomUUID(),
					target: "item",
					collectionId: input.collectionId,
					itemId: input.itemId,
					side: input.side,
					file: input.file,
					previewUrl: input.previewUrl,
				},
			});
			return { queuedMutations: next };
		});
	},
	upsertSkuGraphic: (input) => {
		set((state) => {
			let replacedPreview: string | null = null;
			const next = state.queuedMutations.filter((mutation) => {
				if (mutation.type !== "patch-item-graphics") return true;
				const match =
					mutation.command.target === "sku" &&
					mutation.command.collectionId === input.collectionId &&
					mutation.command.sku === input.sku &&
					mutation.command.side === input.side;
				if (match) {
					replacedPreview = mutation.command.previewUrl;
				}
				return !match;
			});
			if (replacedPreview) URL.revokeObjectURL(replacedPreview);
			next.push({
				id: crypto.randomUUID(),
				type: "patch-item-graphics",
				command: {
					id: crypto.randomUUID(),
					target: "sku",
					collectionId: input.collectionId,
					itemId: input.itemId,
					sku: input.sku,
					side: input.side,
					file: input.file,
					previewUrl: input.previewUrl,
				},
			});
			return { queuedMutations: next };
		});
	},
	findPreviewUrl: (collectionId, itemId, sku, side) => {
		const mutations = get().queuedMutations;
		for (let index = mutations.length - 1; index >= 0; index -= 1) {
			const mutation = mutations[index];
			if (mutation.type !== "patch-item-graphics") continue;
			const command = mutation.command;
			if (command.collectionId !== collectionId || command.side !== side) continue;
			if (command.target === "item" && command.itemId === itemId) return command.previewUrl;
			if (command.target === "sku" && sku && command.sku === sku) return command.previewUrl;
		}
		return null;
	},
	clearQueue: () => {
		set((state) => {
			for (const mutation of state.queuedMutations) revokePreview(mutation);
			return { queuedMutations: [] };
		});
	},
	clearCollectionQueue: (collectionId) => {
		set((state) => {
			const next = state.queuedMutations.filter((mutation) => {
				if (
					mutation.type === "patch-item-graphics" &&
					mutation.command.collectionId === collectionId
				) {
					revokePreview(mutation);
					return false;
				}
				if ("collectionId" in mutation) {
					return mutation.collectionId !== collectionId;
				}
				return mutation.type !== "create-collection";
			});
			return { queuedMutations: next };
		});
	},
}));
